import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SkillCard from "../components/SkillCard";
import SkillFilter from "../components/SkillFilter";
import SkillDemo from "../components/SkillDemo";
import { skills, SKILLS_CATEGORIES } from "../data/skills";
import SEOHead from "../components/SEOHead";

export default function SkillsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");
  const [demoSkill, setDemoSkill] = useState(null);

  const filteredSkills = useMemo(() => {
    if (activeCategory === "all") return skills;
    return skills.filter((s) => s.category === activeCategory);
  }, [activeCategory]);

  const categoryLabel =
    SKILLS_CATEGORIES.find((c) => c.id === activeCategory)?.label || t('skills_page.all_categories');

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title="Skills Marketplace | Create & Sell AI Skills"
        description="Browse, create, and sell AI skills. Extend your AI agents with community-built skills for support, legal, finance, sales, and more."
        url="/skills"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
            { "@type": "ListItem", position: 2, name: "Skills", item: "https://maxemployee.com/skills" }
            ]
          }
        ]}
      />
      <Header />

      {/* Hero */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid-bg relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">
            // {t('skills_page.title').toUpperCase()}
          </div>
          <h1 className="font-display text-5xl md:text-6xl uppercase leading-[0.95]">
            {t('skills_page.title')}<br />
            <span className="text-amber-500">{t('deploy.agent')}</span>
          </h1>
          <p className="text-zinc-400 mt-6 max-w-xl text-lg">
            {t('skills_page.subtitle')}
          </p>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="mb-8">
          <SkillFilter
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
        </div>

        <div className="font-mono text-xs text-zinc-600 mb-6">
          {filteredSkills.length} {filteredSkills.length === 1 ? "skill" : "skills"}
          {activeCategory !== "all" && ` em ${categoryLabel}`}
        </div>

        {filteredSkills.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSkills.map((skill) => (
              <SkillCard
                key={skill.id}
                skill={skill}
                onDemo={setDemoSkill}
                onBuy={(s) => {
                  if (s.isFree) {
                    alert(`${s.name} ${t('skills_page.added').toLowerCase()}!`);
                  } else {
                    navigate(`/checkout?skillId=${s.id}`);
                  }
                }}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="font-mono text-sm text-zinc-600">
              {t('skills_page.no_skills_found')}
            </p>
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 text-center">
          <h2 className="font-display text-3xl md:text-4xl uppercase mb-4">
            {t('faq_page.still_have_questions')}
          </h2>
          <p className="text-zinc-400 max-w-lg mx-auto mb-8">
            {t('skills_page.subtitle')}
          </p>
          <a
            href="/contact"
            className="inline-block bg-amber-500 hover:bg-amber-400 text-black px-8 py-3 font-mono text-xs uppercase tracking-wider transition-colors"
          >
            {t('marketplace_page.talk_to_team')}
          </a>
        </div>
      </section>

      <Footer />

      {demoSkill && (
        <SkillDemo skill={demoSkill} onClose={() => setDemoSkill(null)} />
      )}
    </div>
  );
}
